import React, { useCallback, useMemo } from 'react';
import { ControlProps } from '@jsonforms/core';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useEndpointControl } from '../shared/useEndpointControl';
import './UseEndpointNumberControl.styles.scss';

/**
 * Number control for fields with use_endpoint (fetch-from-endpoint button).
 * Registered with a tester that matches number + use_endpoint so it only runs for those fields.
 */
export const UseEndpointNumberControl = withJsonFormsControlProps((props: ControlProps) => {
    const { data, handleChange, path, label, required, errors, schema, uischema } = props;
    const ctx = useJsonForms();
    const formData = ctx?.core?.data;

    const {
        useEndpoint,
        showError,
        helperText,
        handleFetchClick,
        isFetching,
        hasUseEndpoint,
    } = useEndpointControl({
        path,
        uischema,
        schema,
        errors,
        handleChange,
        formData,
    });

    const handleChangeEvent = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            handleChange(path, value === '' ? '' : Number(value));
        },
        [handleChange, path]
    );

    const endAdornment = hasUseEndpoint ? (
        <InputAdornment position="end">
            <IconButton
                aria-label={`Fetch value from ${useEndpoint}`}
                onClick={handleFetchClick}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                size="small"
                disabled={isFetching}
            >
                <Refresh fontSize="small" />
            </IconButton>
        </InputAdornment>
    ) : undefined;

    const slotProps = useMemo(
        () => ({
            input: {
                step: schema?.type === 'number' ? 0.1 : 1,
                required: false,
                ...(endAdornment ? { endAdornment } : {}),
            },
        }),
        [schema?.type, endAdornment]
    );

    return (
        <div className="rpc-use-endpoint-number-control">
            <TextField
                type="number"
                value={data ?? ''}
                onChange={handleChangeEvent}
                label={label}
                required={required}
                error={showError}
                helperText={helperText}
                variant="outlined"
                fullWidth
                slotProps={slotProps}
            />
        </div>
    );
});
